// Phase 17 vertical-slice: Rock-Paper-Scissors throwaway harness.
//
// NOT a real game; not registered as a tile. Reachable only via
// https://nostr.gives/#/mp-rps-spike (host) or /#/mp-rps-spike/<roomId>
// (guest). DELETED after slice acceptance per
// `Vault/Runbook/Phase 17 - Multiplayer Foundation.md > 17.0.7`.
//
// Goal: prove the foundation end-to-end -
//   1. host opens /#/mp-rps-spike, clicks Create, gets a 6-char invite code.
//   2. guest opens /#/mp-rps-spike/<code>, types their pseudo-pubkey, joins.
//   3. both pick rock/paper/scissors.
//   4. both see the winner + room.status transition to `finished`.
//
// Pseudo-pubkey: a per-tab random hex string stored in sessionStorage.
// Real production multiplayer uses NIP-46/NIP-07; the spike uses anon
// pubkeys so we can drive the smoke test with two browser tabs without
// needing the login modal wired into the multiplayer scope expansion.
//
// State sync model for the spike: poll `GET /state` every 1500ms while
// the modal is open. The runbook calls for kind-30482 relay subscription
// in the real multiplayer flow; the spike uses HTTP polling to keep the
// throwaway tiny. The Worker route is identical between the two paths.

import { apiJson } from '../../lib/api';

const SPIKE_PUBKEY_KEY = 'gives.mp-rps-spike.pubkey';
const SPIKE_SESSION_KEY = (rid: string): string =>
  `gives.mp-rps-spike.session.${rid}`;
const POLL_INTERVAL_MS = 1500;
const HEARTBEAT_INTERVAL_MS = 20_000;

interface SpikeSession {
  roomId: string;
  token: string;
  playerIdx: 0 | 1;
}

function loadSession(roomId: string): SpikeSession | null {
  try {
    const raw = sessionStorage.getItem(SPIKE_SESSION_KEY(roomId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SpikeSession>;
    if (
      parsed &&
      typeof parsed.token === 'string' &&
      typeof parsed.roomId === 'string' &&
      (parsed.playerIdx === 0 || parsed.playerIdx === 1)
    ) {
      return {
        roomId: parsed.roomId,
        token: parsed.token,
        playerIdx: parsed.playerIdx,
      };
    }
  } catch {
    /* empty */
  }
  return null;
}

function saveSession(s: SpikeSession): void {
  try {
    sessionStorage.setItem(SPIKE_SESSION_KEY(s.roomId), JSON.stringify(s));
  } catch {
    /* empty */
  }
}

interface RoomState {
  id: string;
  gameId: string;
  hostPubkey: string;
  guestPubkey: string | null;
  status: 'waiting' | 'active' | 'finished' | 'abandoned';
  winnerIdx: number | null;
  currentTurnIdx: number;
  currentMoveNonce: number;
  createdAt: number;
  startedAt: number | null;
  endedAt: number | null;
  lastMoveAt: number | null;
  lastHeartbeatAt: number | null;
  expiresAt: number;
}

interface HistoryEntry {
  moveNonce: number;
  playerIdx: number;
  move: unknown;
  acceptedAt: number;
}

interface StateResponse {
  room: RoomState;
  history: HistoryEntry[];
}

interface CreateResponse {
  roomId: string;
  gameId: string;
  sessionToken: string;
  playerIdx: number;
  expiresAt: number;
}

interface JoinResponse {
  roomId: string;
  gameId: string;
  sessionToken: string;
  playerIdx: number;
  room: RoomState | null;
}

interface MoveResponse {
  ok: true;
  moveNonce: number;
  room: RoomState | null;
}

// ---------------------------------------------------------------------------
// Per-tab anon pubkey
// ---------------------------------------------------------------------------
function getOrCreateAnonPubkey(): string {
  try {
    const existing = sessionStorage.getItem(SPIKE_PUBKEY_KEY);
    if (existing && /^[0-9a-f]{64}$/.test(existing)) return existing;
  } catch {
    /* empty */
  }
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  try {
    sessionStorage.setItem(SPIKE_PUBKEY_KEY, hex);
  } catch {
    /* empty */
  }
  return hex;
}

// ---------------------------------------------------------------------------
// Module entry point. main.ts calls this when the hash matches
// `#/mp-rps-spike` or `#/mp-rps-spike/<roomId>`.
// ---------------------------------------------------------------------------
export interface SpikeHandle {
  unmount(): void;
}

export function mountRpsSpike(
  mount: HTMLElement,
  initialRoomId: string | null,
): SpikeHandle {
  mount.innerHTML = '';
  const root = document.createElement('section');
  root.className = 'mp-rps-spike';
  root.style.padding = '24px';
  root.style.maxWidth = '640px';
  root.style.margin = '0 auto';
  root.style.fontFamily = 'system-ui, sans-serif';
  mount.appendChild(root);

  const heading = document.createElement('h1');
  heading.textContent = 'Multiplayer RPS Spike';
  heading.style.marginBottom = '4px';
  root.appendChild(heading);

  const sub = document.createElement('p');
  sub.style.color = 'var(--color-fg-muted, #888)';
  sub.style.marginTop = '0';
  sub.textContent =
    'Phase 17 vertical slice. Throwaway harness. Open two tabs to play.';
  root.appendChild(sub);

  const me = getOrCreateAnonPubkey();
  const meLine = document.createElement('p');
  meLine.style.fontFamily = 'ui-monospace, monospace';
  meLine.style.fontSize = '12px';
  meLine.style.color = 'var(--color-fg-muted, #888)';
  meLine.textContent = `me: ${me.slice(0, 12)}...`;
  root.appendChild(meLine);

  const stage = document.createElement('div');
  stage.style.marginTop = '16px';
  root.appendChild(stage);

  const log = document.createElement('pre');
  log.style.background = 'var(--color-surface-subtle, #f3f3f3)';
  log.style.padding = '12px';
  log.style.fontSize = '12px';
  log.style.maxHeight = '200px';
  log.style.overflow = 'auto';
  log.style.marginTop = '16px';
  log.style.whiteSpace = 'pre-wrap';
  log.dataset.testid = 'mp-rps-log';
  root.appendChild(log);

  function append(line: string): void {
    log.textContent = `${new Date().toISOString().slice(11, 19)}  ${line}\n${log.textContent ?? ''}`;
  }

  let pollHandle: number | null = null;
  let heartbeatHandle: number | null = null;
  let unmounted = false;

  function stopTimers(): void {
    if (pollHandle !== null) {
      window.clearInterval(pollHandle);
      pollHandle = null;
    }
    if (heartbeatHandle !== null) {
      window.clearInterval(heartbeatHandle);
      heartbeatHandle = null;
    }
  }

  async function pollState(
    roomId: string,
    onState: (snap: StateResponse) => void,
  ): Promise<void> {
    const res = await apiJson<StateResponse>(
      `/api/multiplayer/mp-rps-spike/room/${roomId}/state`,
    );
    if (res.status === 200 && res.data) onState(res.data);
  }

  function startPolling(
    roomId: string,
    onState: (snap: StateResponse) => void,
  ): void {
    pollHandle = window.setInterval(() => {
      if (unmounted) return;
      void pollState(roomId, onState);
    }, POLL_INTERVAL_MS);
    void pollState(roomId, onState);
  }

  function startHeartbeat(roomId: string, token: string): void {
    heartbeatHandle = window.setInterval(() => {
      void apiJson<{ ok: true }>(
        `/api/multiplayer/mp-rps-spike/room/${roomId}/heartbeat`,
        { method: 'POST', json: { sessionToken: token } },
      );
    }, HEARTBEAT_INTERVAL_MS);
  }

  // -------------------------------------------------------------------------
  // Stage renderers
  // -------------------------------------------------------------------------

  function renderLobby(prefillJoinId: string | null): void {
    stage.innerHTML = '';
    const ctrls = document.createElement('div');
    ctrls.style.display = 'flex';
    ctrls.style.flexDirection = 'column';
    ctrls.style.gap = '12px';

    const createBtn = document.createElement('button');
    createBtn.textContent = 'Create room';
    createBtn.dataset.testid = 'mp-rps-create';
    createBtn.style.padding = '12px 16px';
    createBtn.style.fontSize = '16px';
    ctrls.appendChild(createBtn);

    const orLabel = document.createElement('p');
    orLabel.textContent = 'or';
    orLabel.style.margin = '0';
    orLabel.style.textAlign = 'center';
    orLabel.style.color = 'var(--color-fg-muted, #888)';
    ctrls.appendChild(orLabel);

    const joinRow = document.createElement('div');
    joinRow.style.display = 'flex';
    joinRow.style.gap = '8px';
    const joinInput = document.createElement('input');
    joinInput.type = 'text';
    joinInput.placeholder = '6-char code';
    joinInput.maxLength = 6;
    joinInput.style.flex = '1';
    joinInput.style.padding = '12px';
    joinInput.style.fontSize = '16px';
    joinInput.style.textTransform = 'uppercase';
    joinInput.dataset.testid = 'mp-rps-code';
    if (prefillJoinId) joinInput.value = prefillJoinId;
    const joinBtn = document.createElement('button');
    joinBtn.textContent = 'Join';
    joinBtn.dataset.testid = 'mp-rps-join';
    joinBtn.style.padding = '12px 16px';
    joinBtn.style.fontSize = '16px';
    joinRow.appendChild(joinInput);
    joinRow.appendChild(joinBtn);
    ctrls.appendChild(joinRow);
    stage.appendChild(ctrls);

    createBtn.addEventListener('click', async () => {
      createBtn.disabled = true;
      append('POST /room ...');
      const res = await apiJson<CreateResponse>(
        '/api/multiplayer/mp-rps-spike/room',
        { method: 'POST', json: { hostPubkey: me } },
      );
      if (res.status !== 200 || !res.data) {
        append(`create failed: ${res.status}`);
        createBtn.disabled = false;
        return;
      }
      const { roomId, sessionToken } = res.data;
      // history.replaceState updates the URL WITHOUT firing hashchange,
      // so we keep our mounted spike and don't re-route to spectator mode.
      try {
        history.replaceState({}, '', `#/mp-rps-spike/${roomId}`);
      } catch {
        /* empty */
      }
      saveSession({ roomId, token: sessionToken, playerIdx: 0 });
      append(`room created: ${roomId}`);
      renderRoom({
        roomId,
        token: sessionToken,
        myIdx: 0,
        share: true,
      });
    });

    joinBtn.addEventListener('click', async () => {
      const code = joinInput.value.trim().toUpperCase();
      if (code.length !== 6) {
        append('code must be 6 chars');
        return;
      }
      joinBtn.disabled = true;
      append(`POST /room/${code}/join ...`);
      const res = await apiJson<JoinResponse>(
        `/api/multiplayer/mp-rps-spike/room/${code}/join`,
        { method: 'POST', json: { guestPubkey: me } },
      );
      if (res.status !== 200 || !res.data) {
        append(`join failed: ${res.status} ${JSON.stringify(res.data)}`);
        joinBtn.disabled = false;
        return;
      }
      try {
        history.replaceState({}, '', `#/mp-rps-spike/${code}`);
      } catch {
        /* empty */
      }
      saveSession({ roomId: code, token: res.data.sessionToken, playerIdx: 1 });
      append(`joined: ${code}`);
      renderRoom({
        roomId: code,
        token: res.data.sessionToken,
        myIdx: 1,
        share: false,
      });
    });

    // Auto-prefill mode: if we landed with a roomId in the hash and we
    // haven't joined yet, focus the code input so the user just clicks
    // Join.
    if (prefillJoinId) joinInput.focus();
  }

  function renderRoom(opts: {
    roomId: string;
    token: string;
    myIdx: 0 | 1;
    share: boolean;
  }): void {
    stopTimers();
    stage.innerHTML = '';

    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';
    header.innerHTML = `<strong>Room <span data-testid="mp-rps-room-id">${opts.roomId}</span></strong><span data-testid="mp-rps-my-idx">you = P${opts.myIdx}</span>`;
    stage.appendChild(header);

    if (opts.share) {
      const shareBox = document.createElement('div');
      shareBox.style.marginTop = '8px';
      shareBox.style.padding = '12px';
      shareBox.style.background = 'var(--color-surface-subtle, #f3f3f3)';
      shareBox.style.borderRadius = '8px';
      shareBox.innerHTML = `Share this URL: <code style="user-select:all">${window.location.origin}/#/mp-rps-spike/${opts.roomId}</code>`;
      stage.appendChild(shareBox);
    }

    const statusLabel = document.createElement('p');
    statusLabel.style.marginTop = '12px';
    statusLabel.dataset.testid = 'mp-rps-status';
    statusLabel.textContent = 'status: ...';
    stage.appendChild(statusLabel);

    const turnLabel = document.createElement('p');
    turnLabel.dataset.testid = 'mp-rps-turn';
    turnLabel.textContent = '';
    stage.appendChild(turnLabel);

    const buttonRow = document.createElement('div');
    buttonRow.style.display = 'flex';
    buttonRow.style.gap = '12px';
    buttonRow.style.marginTop = '16px';
    const mkBtn = (label: string, pick: 'rock' | 'paper' | 'scissors'): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      b.dataset.pick = pick;
      b.dataset.testid = `mp-rps-pick-${pick}`;
      b.style.padding = '14px 20px';
      b.style.fontSize = '18px';
      b.style.flex = '1';
      return b;
    };
    const rockBtn = mkBtn('Rock', 'rock');
    const paperBtn = mkBtn('Paper', 'paper');
    const scissorsBtn = mkBtn('Scissors', 'scissors');
    buttonRow.appendChild(rockBtn);
    buttonRow.appendChild(paperBtn);
    buttonRow.appendChild(scissorsBtn);
    stage.appendChild(buttonRow);

    const outcomeBox = document.createElement('div');
    outcomeBox.dataset.testid = 'mp-rps-outcome';
    outcomeBox.style.marginTop = '16px';
    outcomeBox.style.fontSize = '20px';
    outcomeBox.style.fontWeight = 'bold';
    stage.appendChild(outcomeBox);

    let claimedAt: number | null = null;
    let lastSeenStatus: RoomState['status'] | null = null;

    async function submitPick(pick: 'rock' | 'paper' | 'scissors'): Promise<void> {
      // Disable all pick buttons (one move per player per game).
      rockBtn.disabled = true;
      paperBtn.disabled = true;
      scissorsBtn.disabled = true;
      append(`POST /move pick=${pick}`);
      // Fetch the canonical nonce.
      const state = await apiJson<StateResponse>(
        `/api/multiplayer/mp-rps-spike/room/${opts.roomId}/state`,
      );
      if (!state.data) {
        append('state fetch failed');
        return;
      }
      const claimedNonce = state.data.room.currentMoveNonce + 1;
      const res = await apiJson<MoveResponse>(
        '/api/multiplayer/mp-rps-spike/move',
        {
          method: 'POST',
          json: {
            sessionToken: opts.token,
            claimedNonce,
            move: { pick },
          },
        },
      );
      if (res.status !== 200) {
        append(`move rejected ${res.status} ${JSON.stringify(res.data)}`);
        rockBtn.disabled = false;
        paperBtn.disabled = false;
        scissorsBtn.disabled = false;
        return;
      }
      append(`move accepted moveNonce=${(res.data as MoveResponse).moveNonce}`);
      claimedAt = Date.now();
    }

    rockBtn.addEventListener('click', () => void submitPick('rock'));
    paperBtn.addEventListener('click', () => void submitPick('paper'));
    scissorsBtn.addEventListener('click', () => void submitPick('scissors'));

    startHeartbeat(opts.roomId, opts.token);
    startPolling(opts.roomId, (snap) => {
      const r = snap.room;
      statusLabel.textContent = `status: ${r.status} (turn idx ${r.currentTurnIdx}, nonce ${r.currentMoveNonce})`;
      const myHistoryEntry = snap.history.find((h) => h.playerIdx === opts.myIdx);
      if (myHistoryEntry) {
        // Lock buttons once we've moved.
        rockBtn.disabled = true;
        paperBtn.disabled = true;
        scissorsBtn.disabled = true;
      }
      if (r.status === 'waiting') {
        turnLabel.textContent = 'waiting for opponent to join...';
      } else if (r.status === 'active') {
        if (r.currentTurnIdx === opts.myIdx && !myHistoryEntry) {
          turnLabel.textContent = 'your move';
        } else {
          turnLabel.textContent = 'waiting for opponent...';
        }
      } else if (r.status === 'finished') {
        if (r.winnerIdx === null) {
          outcomeBox.textContent = 'Draw';
          outcomeBox.style.color = 'var(--color-fg-muted, #888)';
        } else if (r.winnerIdx === opts.myIdx) {
          outcomeBox.textContent = 'You won';
          outcomeBox.style.color = 'var(--color-success, #2a9d2a)';
        } else {
          outcomeBox.textContent = 'You lost';
          outcomeBox.style.color = 'var(--color-danger, #c0392b)';
        }
        turnLabel.textContent = '';
        stopTimers();
      } else if (r.status === 'abandoned') {
        outcomeBox.textContent =
          r.winnerIdx === opts.myIdx
            ? 'Opponent abandoned - you win'
            : 'Room abandoned';
        outcomeBox.style.color = 'var(--color-fg-muted, #888)';
        stopTimers();
      }
      if (r.status !== lastSeenStatus) {
        append(`status -> ${r.status}`);
        lastSeenStatus = r.status;
      }
      // Suppress unused-variable warning - kept for future "stop showing
      // the outcome card after 10s" UX.
      void claimedAt;
    });
  }

  // -------------------------------------------------------------------------
  // Entry decision
  // -------------------------------------------------------------------------
  if (initialRoomId) {
    // First, see if we have a stashed JWT for this room (reload / second
    // mount). If so, restore the active player view without re-creating
    // or re-joining.
    const stash = loadSession(initialRoomId);
    if (stash) {
      append(`restoring session as P${stash.playerIdx} in ${stash.roomId}`);
      renderRoom({
        roomId: stash.roomId,
        token: stash.token,
        myIdx: stash.playerIdx,
        share: stash.playerIdx === 0,
      });
    } else {
      // Probe the room first - if it already exists in waiting/active
      // state and isn't ours, render the join form pre-filled.
      void apiJson<StateResponse>(
        `/api/multiplayer/mp-rps-spike/room/${initialRoomId}/state`,
      ).then((res) => {
        if (unmounted) return;
        if (res.status !== 200 || !res.data) {
          append(`room ${initialRoomId} not found, showing lobby`);
          renderLobby(initialRoomId);
          return;
        }
        const r = res.data.room;
        if (r.hostPubkey === me) {
          append('host pubkey matches but JWT is gone - spectating');
          renderSpectator(initialRoomId);
          return;
        }
        if (r.guestPubkey === me) {
          append('guest pubkey matches but JWT is gone - spectating');
          renderSpectator(initialRoomId);
          return;
        }
        renderLobby(initialRoomId);
      });
    }
  } else {
    renderLobby(null);
  }

  function renderSpectator(roomId: string): void {
    stage.innerHTML = '';
    const p = document.createElement('p');
    p.textContent = `Spectating room ${roomId} (re-joining mid-session not yet wired).`;
    stage.appendChild(p);
    startPolling(roomId, (snap) => {
      append(
        `status=${snap.room.status} turnIdx=${snap.room.currentTurnIdx} moves=${snap.history.length}`,
      );
    });
  }

  return {
    unmount(): void {
      unmounted = true;
      stopTimers();
      mount.removeChild(root);
    },
  };
}
